import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip";
import {
  type ConfigListFilterCounts,
  type ConfigListFilters,
  countActiveFilters,
  type MatchMode,
  type ModeOption,
  type ModeSection,
  NO_TEMPLATE,
  type OnOff,
  type OnOffSection,
  serializeFilters,
  TRACKER_FILTER_KEYS,
} from "./helpers/configFilters";

interface ChipOption {
  value: string;
  label: string;
  count?: number;
}

const toggleValue = <T extends string>(values: T[], value: T): T[] =>
  values.includes(value)
    ? values.filter((v) => v !== value)
    : [...values, value];

/** A chip that opens a checklist; options within it are OR. */
const MultiSelectChip = ({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: ChipOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const selectedLabels = options
    .filter((option) => selected.includes(option.value))
    .map((option) => option.label);
  const summary =
    selectedLabels.length === 1
      ? `${label}: ${selectedLabels[0]}`
      : selectedLabels.length > 1
        ? `${label} (${selectedLabels.length})`
        : label;

  return (
    <div className="config-filter-chip" ref={ref}>
      <button
        type="button"
        className="config-filter-chip__button"
        aria-pressed={selected.length > 0}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        {summary}
        <SvgIcon
          src={open ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"}
          alt=""
        />
      </button>
      {open && (
        <ul className="config-filter-chip__menu">
          {options.map((option) => (
            <li key={option.value}>
              <label>
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => onChange(toggleValue(selected, option.value))}
                />
                <span className="config-filter-chip__option-label">
                  {option.label}
                </span>
                {option.count !== undefined && (
                  <span className="config-filter-chip__count">
                    {option.count}
                  </span>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Chips edit a draft; nothing is requested until the coach presses Apply, so
 * picking several options costs one API call instead of one per checkbox.
 */
export const ConfigFilterChips = ({
  referAsClients,
  filters,
  appliedFilters,
  counts,
  shownCount,
  isFiltering,
  onChange,
  onApply,
  onClear,
  search,
}: {
  referAsClients: boolean;
  /** draft — what the chips show */
  filters: ConfigListFilters;
  /** what the table currently reflects */
  appliedFilters: ConfigListFilters;
  /** null until the first response arrives */
  counts: ConfigListFilterCounts | null;
  /** rows currently shown, once filters have been applied */
  shownCount: number | null;
  isFiltering: boolean;
  onChange: (filters: ConfigListFilters) => void;
  onApply: () => void;
  onClear: () => void;
  /** rendered first in the chip row (the name search) */
  search?: ReactNode;
}) => {
  const { t } = useTranslation(["coaching-portal", "athlete-portal"]);
  const activeCount = countActiveFilters(filters);
  const appliedCount = countActiveFilters(appliedFilters);
  const matchLabel = t("coaching-portal:athletes.configFilters.matchLabel", {
    role: referAsClients
      ? t("coaching-portal:athletes.roleClientsLower")
      : t("coaching-portal:athletes.roleAthletesLower"),
  });
  const matchHint = t("coaching-portal:athletes.configFilters.matchHint");
  const hasUnappliedChanges =
    serializeFilters(filters) !== serializeFilters(appliedFilters);
  const listLabel = (key: string) =>
    t(`athlete-portal:clientConfiguration.configList.${key}`);
  const onOffLabels: Record<OnOff, string> = {
    on: t("coaching-portal:athletes.configFilters.on"),
    off: t("coaching-portal:athletes.configFilters.off"),
  };

  const onOffChip = (key: OnOffSection, label: string): React.ReactNode => (
    <MultiSelectChip
      key={key}
      label={label}
      options={(["on", "off"] as OnOff[]).map((value) => ({
        value,
        label: onOffLabels[value],
        count: counts?.sections[key][value],
      }))}
      selected={filters.sections[key] ?? []}
      onChange={(selected) =>
        onChange({
          ...filters,
          sections: { ...filters.sections, [key]: selected as OnOff[] },
        })
      }
    />
  );

  const modeChip = (key: ModeSection, label: string): React.ReactNode => (
    <MultiSelectChip
      key={key}
      label={label}
      options={(["coach", "client", "off"] as ModeOption[]).map((value) => ({
        value,
        // Same wording as the table's chips for this section.
        label: t(
          `athlete-portal:clientConfiguration.configList.sectionModes.${key}.${
            value === "off" ? "hidden" : value
          }`
        ),
        count: counts?.sections[key][value],
      }))}
      selected={filters.sections[key] ?? []}
      onChange={(selected) =>
        onChange({
          ...filters,
          sections: { ...filters.sections, [key]: selected as ModeOption[] },
        })
      }
    />
  );

  const templateOptions: ChipOption[] = (counts?.template ?? []).map(
    ({ id, name, count }) => ({
      value: id,
      label:
        id === NO_TEMPLATE
          ? t("coaching-portal:athletes.configFilters.noTemplate")
          : (name ?? id),
      count,
    })
  );

  return (
    <div className="athletes-config-list__filters">
      <div className="athletes-config-list__filters-chips">
        {search}
        <div className="config-filter-chip">
          <button
            type="button"
            className="config-filter-chip__button config-filter-chip__button--attention"
            aria-pressed={filters.needsAttention}
            onClick={() =>
              onChange({ ...filters, needsAttention: !filters.needsAttention })
            }
          >
            {t("coaching-portal:athletes.configFilters.needsAttention")}
            {counts && (
              <span className="config-filter-chip__count">
                {counts.needsAttention.any}
              </span>
            )}
          </button>
        </div>
        <MultiSelectChip
          label={listLabel("cycle")}
          options={(["on", "off"] as OnOff[]).map((value) => ({
            value,
            label: onOffLabels[value],
            count: counts?.cycle[value],
          }))}
          selected={filters.cycle}
          onChange={(selected) =>
            onChange({ ...filters, cycle: selected as OnOff[] })
          }
        />
        <MultiSelectChip
          label={listLabel("template")}
          options={templateOptions}
          selected={filters.template}
          onChange={(selected) => onChange({ ...filters, template: selected })}
        />
        {onOffChip("training", listLabel("training"))}
        {modeChip("nutrition", listLabel("nutrition"))}
        {modeChip("mealPlans", listLabel("mealPlans"))}
        {modeChip("dailyAccountabilities", listLabel("accountabilities"))}
        {onOffChip("goals", listLabel("goals"))}
        <MultiSelectChip
          label={t("coaching-portal:athletes.configFilters.tracking")}
          options={TRACKER_FILTER_KEYS.map((key) => ({
            value: key,
            label: t(
              `athlete-portal:clientConfiguration.configList.trackingLabels.${key}`
            ),
            count: counts?.trackers[key],
          }))}
          selected={filters.trackers}
          onChange={(selected) =>
            onChange({
              ...filters,
              trackers: selected as ConfigListFilters["trackers"],
            })
          }
        />
        {(activeCount > 0 || appliedCount > 0) && (
          <button
            type="button"
            data-button-type="primary"
            className="athletes-config-list__filters-apply"
            disabled={!hasUnappliedChanges || isFiltering}
            onClick={onApply}
          >
            {t("coaching-portal:athletes.configFilters.apply")}
          </button>
        )}
      </div>

      {(activeCount > 0 || appliedCount > 0) && (
        <div className="athletes-config-list__filters-status">
          {activeCount > 1 && (
            <fieldset className="athletes-config-list__filters-match">
              <legend className="sr-only">{matchLabel}</legend>
              <span
                className="athletes-config-list__filters-match-label"
                aria-hidden="true"
              >
                {matchLabel}
                <ToolTip id="config-filters-match-tooltip" body={matchHint}>
                  <SvgIcon
                    classNames="athletes-config-list__filters-match-hint"
                    style={{}}
                    alt={matchHint}
                    src="/img/new-icons/question-circle.svg"
                  />
                </ToolTip>
              </span>
              {(["all", "any"] as MatchMode[]).map((match) => (
                <button
                  key={match}
                  type="button"
                  aria-pressed={filters.match === match}
                  onClick={() => onChange({ ...filters, match })}
                >
                  {t(
                    match === "all"
                      ? "coaching-portal:athletes.configFilters.matchAll"
                      : "coaching-portal:athletes.configFilters.matchAny"
                  )}
                </button>
              ))}
            </fieldset>
          )}
          <span className="athletes-config-list__filters-result">
            {isFiltering
              ? t("coaching-portal:athletes.configFilters.filtering")
              : hasUnappliedChanges
                ? t("coaching-portal:athletes.configFilters.unapplied")
                : appliedCount > 0 && shownCount !== null && counts
                  ? t("coaching-portal:athletes.configFilters.showing", {
                      shown: shownCount,
                      total: counts.total,
                    })
                  : null}
          </span>
          <button
            type="button"
            className="athletes-config-list__filters-clear"
            onClick={onClear}
          >
            {t("coaching-portal:athletes.configFilters.clearAll")}
          </button>
        </div>
      )}
    </div>
  );
};

// Rough widths of the real chips, so the row doesn't jump when they arrive.
const SKELETON_CHIP_WIDTHS = [9, 5, 6.5, 5.5, 6, 6.5, 9, 4.5, 6, 4.5];

/** Placeholder for the chip row until the first counts arrive. */
export const ConfigFilterChipsSkeleton = ({
  search,
}: {
  /** rendered first in the chip row (the name search) */
  search?: ReactNode;
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <output
      className="athletes-config-list__filters"
      aria-label={t("athletes.configFilters.loadingFilters")}
    >
      <div className="athletes-config-list__filters-chips">
        {search}
        {SKELETON_CHIP_WIDTHS.map((width, i) => (
          <span
            aria-hidden="true"
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list, never reordered
            key={i}
            className="config-filter-chip-skeleton"
            style={{ width: `${width}rem` }}
          />
        ))}
      </div>
    </output>
  );
};
